import {
  DefaultVehicleVersion,
  VehicleVersion,
  VehicleVersionFields,
  VehicleVersionFieldsList,
  VehicleMatch,
} from 'types';

export const emptyVehicleVersion = {
  make: undefined,
  model: undefined,
  category: undefined,
  energy: undefined,
  doors: undefined,
  gearbox: undefined,
  fiscalHorsePower: undefined,
  motorizationFull: undefined,
  trimLevel: undefined,
  cubic: undefined,
  version: undefined,
};

export const emptyFullVehicleVersion: VehicleVersion = {
  firstCirculationDate: undefined,
  make: undefined,
  model: undefined,
  version: undefined,
  category: undefined,
  energy: undefined,
  doors: undefined,
  gearbox: undefined,
  fiscalHorsePower: undefined,
  motorizationFull: undefined,
  trimLevel: undefined,
  cubic: undefined,
  imported: undefined,
  firstHand: undefined,
  mileage: undefined,
  condition: undefined,
  internalColor: undefined,
  externalColor: undefined,
  paintType: undefined,
  seatMaterial: undefined,
  referencePro: undefined,
};

export const createVehicleMatchLists = (matchLists: VehicleMatch[]): VehicleMatch[] => {
  return matchLists.map(match => {
    const {
      versionId,
      make,
      model,
      category,
      energy,
      doors,
      gearbox,
      motorizationFull,
      trimLevel,
      fiscalHorsePower,
      version,
      commercialName,
      seats,
      cubic,
    } = match.item;
    return {
      rank: match.rank,
      item: {
        versionId: versionId.toString(),
        make,
        model,
        category,
        energy,
        doors,
        gearbox,
        fiscalHorsePower,
        version,
        motorizationFull: motorizationFull === undefined || motorizationFull.length === 0 ? 'AUTRE' : motorizationFull,
        trimLevel: !trimLevel || trimLevel.length === 0 ? 'AUCUNE' : trimLevel,
        commercialName,
        seats,
        cubic,
      },
    };
  });
};

export const createVehicleSearchOptionsLists = (matchLists: VehicleMatch[]): VehicleVersionFieldsList => {
  const options: VehicleVersionFieldsList = {
    make: [],
    model: [],
    category: [],
    version: [],
    energy: [],
    doors: [],
    gearbox: [],
    fiscalHorsePower: [],
    motorizationFull: [],
    trimLevel: [],
    cubic: [],
  };

  matchLists.forEach(match => {
    const item = match.item;
    Object.keys(item).forEach(key => {
      const k = key as keyof VehicleVersionFields;
      const value = item[k]?.toString();
      if (value && options[k] && !options[k].includes(value)) options[k].push(value);
    });
  });

  options.category.sort().sort((a, b) => (a < b ? -1 : 1));
  options.energy.sort().sort((a, b) => (a < b ? -1 : 1));
  options.doors.sort((a, b) => Number(a) - Number(b));
  options.gearbox.sort((a, b) => (a < b ? -1 : 1));
  options.fiscalHorsePower.sort((a, b) => Number(a) - Number(b));
  options.motorizationFull.sort((a, b) => (a < b ? -1 : 1));
  options.trimLevel.sort((a, b) => (a < b ? -1 : 1));
  options.cubic.sort((a, b) => Number(a) - Number(b));

  const motorizationIndex = options.motorizationFull.findIndex(value => value === 'AUTRE');
  if (motorizationIndex > -1) {
    options.motorizationFull.splice(motorizationIndex, 1);
    options.motorizationFull = [...options.motorizationFull, 'AUTRE'];
  }

  const trimLevelIndex = options.trimLevel.findIndex(value => value === 'AUCUNE');
  if (trimLevelIndex > -1) {
    options.trimLevel.splice(trimLevelIndex, 1);
    options.trimLevel = ['AUCUNE', ...options.trimLevel];
  }

  return options;
};

export const selectFilterBySelectedVersion = (
  matchLists: VehicleMatch[],
  selectedVersion: string,
): VehicleVersionFields => {
  // Select only fields of interface VehicleVersionFields
  const {
    make,
    model,
    category,
    energy,
    doors,
    gearbox,
    motorizationFull,
    trimLevel,
    fiscalHorsePower,
    cubic,
    version,
  } = matchLists.find(match => match.item.version === selectedVersion)?.item ?? matchLists[0].item;
  return {
    make,
    model,
    category,
    energy,
    doors,
    gearbox,
    motorizationFull,
    trimLevel,
    fiscalHorsePower,
    cubic,
    version,
  };
};

export const getEligibleVersionsForFilter = (
  matchLists: VehicleMatch[],
  filteredVehicleVersion: VehicleVersionFields,
): VehicleVersionFieldsList => {
  // remove undefined
  const extractedFields = Object.keys(emptyVehicleVersion)
    .filter(k => filteredVehicleVersion[k as keyof VehicleVersionFields] != null)
    .reduce<Partial<VehicleVersionFields>>(
      (a, k) => ({ ...a, [k]: filteredVehicleVersion[k as keyof VehicleVersionFields] }),
      {},
    );

  const eligibleMatchs = matchLists.reduce<VehicleMatch[]>((eligibles, match) => {
    for (const k of Object.keys(extractedFields)) {
      const key = k as keyof VehicleVersionFields;

      // eslint-disable-next-line eqeqeq
      if (match.item[key] != extractedFields[key]) {
        return eligibles;
      }
    }
    eligibles.push(match);
    return eligibles;
  }, []);
  return createVehicleSearchOptionsLists(eligibleMatchs);
};

// Priority : Category > Energy > Doors > Gearbox > FiscalHorsePower
export const getValidDefaultVehicleVersion = (
  defaultVehicleVersion: DefaultVehicleVersion,
  matchLists: VehicleMatch[],
): DefaultVehicleVersion => {
  const { category, energy, doors, gearbox, fiscalHorsePower } = defaultVehicleVersion;
  const extractedFields = { category, energy, doors, gearbox, fiscalHorsePower };
  const list = getEligibleVersionsForFilter(matchLists, extractedFields);
  if (list.version.length > 0) return getValidDefaultVehicle(list, defaultVehicleVersion);

  let newDefaultVehicleVersion = {
    category: undefined,
    energy: undefined,
    doors: undefined,
    gearbox: undefined,
    fiscalHorsePower: undefined,
  };
  for (const k of Object.keys(extractedFields)) {
    const key = k as keyof DefaultVehicleVersion;
    if (
      extractedFields[key] &&
      getEligibleVersionsForFilter(matchLists, { ...newDefaultVehicleVersion, [k]: extractedFields[key] })[key].length >
        0
    ) {
      newDefaultVehicleVersion = { ...newDefaultVehicleVersion, [k]: extractedFields[key] };
    } else {
      break;
    }
  }

  return newDefaultVehicleVersion;
};

// Priority : Make > Model > Category > Energy > Doors > Gearbox > Motorization > TrimLevel > FiscalHorsePower
export const getValidEligibleFields = (
  matchLists: VehicleMatch[],
  newValues: VehicleVersionFields,
  oldValues: VehicleVersionFields,
): VehicleVersionFields => {
  const fieldsList = getEligibleVersionsForFilter(matchLists, newValues);
  const values = { ...oldValues, ...newValues };

  const newDefaultVehicleVersion = Object.keys(emptyVehicleVersion).reduce<Partial<VehicleVersionFields>>((a, k) => {
    const key = k as keyof VehicleVersionFields;
    const previousList = getEligibleVersionsForFilter(matchLists, a);
    const list = getEligibleVersionsForFilter(matchLists, { ...a, [k]: values[key] });
    if (fieldsList[key].length === 1) {
      return { ...a, [k]: fieldsList[key][0] };
    } else if (values[key] !== undefined && fieldsList[key].includes(values[key]!.toString()) && list[key].length > 0) {
      return { ...a, [k]: values[key] };
    } else if (previousList[key].length === 1) {
      return { ...a, [k]: previousList[key][0] };
    } else {
      return { ...a, [k]: undefined };
    }
  }, newValues);

  return newDefaultVehicleVersion;
};

export const getValidDefaultVehicle = (
  fieldsList: VehicleVersionFieldsList,
  defaultVehicle: VehicleVersion,
): VehicleVersion => {
  return {
    make: fieldsList.make.length === 1 ? fieldsList.make[0] : defaultVehicle.make,
    model: fieldsList.model.length === 1 ? fieldsList.model[0] : defaultVehicle.model,
    category: fieldsList.category.length === 1 ? fieldsList.category[0] : defaultVehicle.category,
    energy: fieldsList.energy.length === 1 ? fieldsList.energy[0] : defaultVehicle.energy,
    doors: fieldsList.doors.length === 1 ? Number.parseInt(fieldsList.doors[0]) : defaultVehicle.doors,
    gearbox: fieldsList.gearbox.length === 1 ? fieldsList.gearbox[0] : defaultVehicle.gearbox,
    motorizationFull:
      fieldsList.motorizationFull.length === 1 ? fieldsList.motorizationFull[0] : defaultVehicle.motorizationFull,
    trimLevel: fieldsList.trimLevel.length === 1 ? fieldsList.trimLevel[0] : defaultVehicle.trimLevel,
    cubic: fieldsList.cubic.length === 1 ? Number.parseInt(fieldsList.cubic[0]) : defaultVehicle.cubic,
    fiscalHorsePower:
      fieldsList.fiscalHorsePower.length === 1
        ? Number.parseInt(fieldsList.fiscalHorsePower[0])
        : defaultVehicle.fiscalHorsePower,
  };
};

export const isImported = (origin?: string): boolean | undefined => {
  if (origin === 'FRENCH') return false;
  else if (origin === 'NON_EUROPEAN') return true;
  return undefined;
};
